import { useState, useEffect, useRef } from 'react';
import { FileText, ExternalLink, X, Loader2 } from 'lucide-react';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';

const FileUploadStep = ({ onSubmit, onBack, permitChatId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploads, setUploads] = useState({});
  const fileRefs = useRef({});

  useEffect(() => {
    setIsLoading(true);
    appAxios
      .get('/documents/required', { params: { permitChatId } })
      .then(res => setDocuments(res.data?.data ?? res.data ?? []))
      .catch(() => toast.error('Failed to load required documents.'))
      .finally(() => setIsLoading(false));
  }, [permitChatId]);

  const handleFileChange = (docName, e) => {
    const file = e.target.files[0];
    if (file) setUploads(prev => ({ ...prev, [docName]: file }));
    e.target.value = '';
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(uploads).forEach(([name, file]) => {
        if (file) formData.append(name, file);
      });
      await appAxios.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { permitChatId },
      });
      toast.success('Documents submitted successfully.');
      onSubmit();
    } catch {
      toast.error('Failed to submit documents.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-sm">Loading required documents…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full">
      {documents.length === 0 ? (
        <p className="text-center text-sm text-gray-500 py-6">No documents required.</p>
      ) : (
        <div className="max-h-[260px] overflow-y-auto space-y-3 pr-1 no-scrollbar">
          {documents.map(doc => (
            <div key={doc.name} className="rounded border border-gray-700 p-3 space-y-2 bg-black/30">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-white">{doc.name}</span>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 shrink-0"
                >
                  Open form <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              {uploads[doc.name] ? (
                <div className="flex items-center gap-2 bg-gray-800 px-2 py-1.5 rounded text-xs text-gray-300">
                  <FileText className="w-3 h-3 shrink-0" />
                  <span className="truncate flex-1">{uploads[doc.name].name}</span>
                  <button
                    onClick={() => setUploads(prev => { const n = { ...prev }; delete n[doc.name]; return n; })}
                    className="text-gray-500 hover:text-white transition"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    ref={el => (fileRefs.current[doc.name] = el)}
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.txt,image/*"
                    onChange={e => handleFileChange(doc.name, e)}
                  />
                  <button
                    type="button"
                    onClick={() => fileRefs.current[doc.name]?.click()}
                    className="w-full border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 py-2 rounded text-xs transition"
                  >
                    Choose file
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <button
        onClick={handleSubmit}
        disabled={isSubmitting || documents.length === 0}
        className="w-full bg-[#7B7B7B] hover:bg-gray-500 text-white py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-2"
      >
        {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {isSubmitting ? 'Uploading…' : 'Continue'}
      </button>
      {/* <button onClick={onBack} className="w-full border border-gray-700 hover:border-gray-500 text-white py-3 rounded-lg transition text-sm">Back</button> */}
    </div>
  );
};

export default FileUploadStep;
